import { useEffect, useState } from "react";
import api, { money, shortDate, STATUS_STYLES } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { TargetSummaryCard } from "@/components/TargetProgress";
import RollingForecast from "@/components/RollingForecast";
import { Link } from "react-router-dom";
import {
  TrendingUp, TrendingDown, DollarSign, Store, AlertCircle,
  CheckCircle2, ClipboardCheck, UserPlus, Repeat, CalendarClock,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Cell,
} from "recharts";

const KpiCard = ({ label, value, delta, icon: Icon, tone = "default", testid }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5 card-hoverable" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div className="text-xs uppercase tracking-[0.08em] font-semibold text-slate-500">{label}</div>
      {Icon && <Icon className={`w-4 h-4 ${tone === "red" ? "text-[#003DA5]" : "text-slate-400"}`} />}
    </div>
    <div className="kpi-value text-3xl sm:text-4xl mt-3">{value}</div>
    {delta !== undefined && delta !== null && (
      <div className={`mt-2 text-xs font-semibold flex items-center gap-1 ${delta >= 0 ? "text-emerald-600" : "text-red-600"}`}>
        {delta >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
        {delta >= 0 ? "+" : ""}{delta}% vs last month
      </div>
    )}
  </div>
);

const SectionCard = ({ title, children, action }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-heading font-semibold">{title}</h3>
      {action}
    </div>
    {children}
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const { user } = useAuth();
  const isAgent = user?.role === "sales_agent";
  const [month, setMonth] = useState(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`;
  });
  const [state, setState] = useState("");
  const [agentId, setAgentId] = useState("");
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/agents").then(r => setAgents(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { month };
    if (state) params.state = state;
    if (agentId) params.agent_id = agentId;
    api.get("/dashboard", { params })
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [month, state, agentId]);

  if (loading || !data) return <div className="text-slate-500">Loading dashboard…</div>;

  const k = data.kpis;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {isAgent && (
        <div>
          <h2 className="text-lg font-heading font-semibold mb-3 text-[#001F54]">My Target</h2>
          <TargetSummaryCard />
        </div>
      )}
      {!isAgent && <RollingForecast />}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-black tracking-tight">Dashboard</h1>
          <p className="text-slate-600 mt-1">Live overview of sales, visits & store activity</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="h-11 px-3 border border-slate-300 rounded-lg bg-white focus:border-[#003DA5] outline-none"
            data-testid="dashboard-month-filter"
          />
          <select value={state} onChange={(e) => setState(e.target.value)} className="h-11 px-3 border border-slate-300 rounded-lg bg-white" data-testid="dashboard-state-filter">
            <option value="">All States</option>
            {["NSW", "VIC", "QLD", "SA", "WA", "TAS", "NT", "ACT"].map(s => <option key={s}>{s}</option>)}
          </select>
          <select value={agentId} onChange={(e) => setAgentId(e.target.value)} className="h-11 px-3 border border-slate-300 rounded-lg bg-white" data-testid="dashboard-agent-filter">
            <option value="">All Agents</option>
            {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 stagger">
        <KpiCard testid="kpi-sales-month" label="Sales This Month" value={money(k.sales_this_month)} delta={k.growth_pct} icon={DollarSign} tone="red" />
        <KpiCard testid="kpi-sales-last-month" label="Sales Last Month" value={money(k.sales_last_month)} icon={DollarSign} />
        <KpiCard testid="kpi-sales-ly" label="Same Month Last Year" value={money(k.sales_last_year_same_month)} icon={DollarSign} />
        <KpiCard testid="kpi-active-stores" label="Active Stores" value={k.active_stores} icon={CheckCircle2} />
        <KpiCard testid="kpi-no-sale-stores" label="No-Sale Stores" value={k.no_sale_stores} icon={AlertCircle} />
        <KpiCard testid="kpi-inactive-stores" label="Inactive Stores" value={k.inactive_stores} icon={Store} />
        <KpiCard testid="kpi-new-stores" label="New Stores" value={k.new_stores} icon={UserPlus} />
        <KpiCard testid="kpi-returning-stores" label="Returning Stores" value={k.returning_stores} icon={Repeat} />
        <KpiCard testid="kpi-visits" label="Store Visits Completed" value={k.visits_completed} icon={ClipboardCheck} />
        <KpiCard testid="kpi-overdue" label="Follow-ups Overdue" value={k.overdue_followups} icon={CalendarClock} tone="red" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Top 5 Sales Agents">
          {data.top_agents.length === 0 ? <div className="text-sm text-slate-500">No sales data.</div> :
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.top_agents} layout="vertical" margin={{ left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={110} />
                <Tooltip formatter={(v) => money(v)} />
                <Bar dataKey="total" radius={[0, 6, 6, 0]}>
                  {data.top_agents.map((_, i) => <Cell key={i} fill="#003DA5" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>}
        </SectionCard>

        <SectionCard title="Top 5 Customers">
          {data.top_customers.length === 0 ? <div className="text-sm text-slate-500">No sales data.</div> :
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.top_customers} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={150} />
                <Tooltip formatter={(v) => money(v)} />
                <Bar dataKey="total" fill="#001F54" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>}
        </SectionCard>

        <SectionCard title="Top 5 Towns">
          <ul className="space-y-2">
            {data.top_towns.map((t, i) => (
              <li key={i} className="flex items-center justify-between text-sm py-1.5 border-b last:border-0 border-slate-100">
                <span className="font-medium">{t.name}</span><span className="font-semibold">{money(t.total)}</span>
              </li>
            ))}
            {data.top_towns.length === 0 && <div className="text-sm text-slate-500">No sales data.</div>}
          </ul>
        </SectionCard>

        <SectionCard title="Top 5 States">
          <ul className="space-y-2">
            {data.top_states.map((t, i) => (
              <li key={i} className="flex items-center justify-between text-sm py-1.5 border-b last:border-0 border-slate-100">
                <span className="font-medium">{t.name}</span><span className="font-semibold">{money(t.total)}</span>
              </li>
            ))}
            {data.top_states.length === 0 && <div className="text-sm text-slate-500">No sales data.</div>}
          </ul>
        </SectionCard>

        <SectionCard title="Customers with Declining Sales">
          <ul className="space-y-2">
            {data.declining_customers.map((c) => (
              <li key={c.id} className="flex items-center justify-between text-sm py-1.5 border-b last:border-0 border-slate-100">
                <Link to={`/customers/${c.id}`} className="font-medium hover:text-[#003DA5]">{c.name}</Link>
                <span className="text-red-600 font-semibold">{c.delta_pct}%</span>
              </li>
            ))}
            {data.declining_customers.length === 0 && <div className="text-sm text-slate-500">No declining customers.</div>}
          </ul>
        </SectionCard>

        <SectionCard title="Customers Requiring Follow-up">
          <ul className="space-y-2">
            {data.followup_required.map((c) => (
              <li key={c.id} className="flex items-center justify-between text-sm py-1.5 border-b last:border-0 border-slate-100">
                <Link to={`/customers/${c.id}`} className="font-medium hover:text-[#003DA5]">{c.name}</Link>
                <span className="text-slate-500">{c.town}</span>
              </li>
            ))}
            {data.followup_required.length === 0 && <div className="text-sm text-slate-500">None to follow-up.</div>}
          </ul>
        </SectionCard>

        <SectionCard title="Recent Store Visits">
          <ul className="divide-y divide-slate-100">
            {data.recent_visits.map((v) => (
              <li key={v.id} className="py-2 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">{v.customer_name}</div>
                  <div className="text-xs text-slate-500">{v.agent_name} • {shortDate(v.visit_date)}</div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${STATUS_STYLES[v.visit_outcome] || "badge-info"}`}>{v.visit_outcome || "Recorded"}</span>
              </li>
            ))}
            {data.recent_visits.length === 0 && <div className="text-sm text-slate-500">No visits yet.</div>}
          </ul>
        </SectionCard>

        <SectionCard title="Recent Store Photos">
          {data.recent_photos.length === 0 ? <div className="text-sm text-slate-500">No photos uploaded yet.</div> :
            <div className="grid grid-cols-3 gap-2">
              {data.recent_photos.map((p) => (
                <PhotoThumb key={p.id} photo={p} />
              ))}
            </div>
          }
        </SectionCard>
      </div>
    </div>
  );
}

function PhotoThumb({ photo }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get(`/photos/${photo.id}/download`, { responseType: "blob" }).then(r => {
      if (cancelled) return;
      setUrl(URL.createObjectURL(r.data));
    }).catch(() => {});
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photo.id]);
  return (
    <div className="aspect-square bg-slate-100 rounded overflow-hidden">
      {url && <img src={url} alt={photo.category} className="w-full h-full object-cover" />}
    </div>
  );
}
